import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { LEGAL_DOCS } from "@/legal/content";
import { api } from "@/lib/api";

// Footer-only legal slugs (kept short per product decision).
const FOOTER_LEGAL_SLUGS = [
  "terms-of-service",
  "privacy-policy",
  "cookie-policy",
  "subscription-refund-policy",
];
import { Youtube, Instagram, Facebook, Linkedin, Twitter, Globe } from "lucide-react";

const ICONS = {
  youtube: Youtube,
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
  twitter: Twitter,
};

export default function Footer() {
  const { user } = useAuth();
  const [social, setSocial] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/platform/social-links");
        if (alive) setSocial(data.rows || []);
      } catch (e) {
        console.error("load social-links failed", e);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Hide the marketing footer entirely once the user is signed in.
  if (user) return null;
  return (
    <footer className="border-t border-ec-line bg-ec-soft mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 grid grid-cols-2 md:grid-cols-6 gap-10">
        <div className="col-span-2">
          <Logo size={36} />
          <p className="mt-4 max-w-sm text-ec-ink-soft text-sm leading-relaxed">
            The Professional Passport for life. Build trust. Verify experience. Grow your career —
            backed by an immutable, globally-verifiable credibility infrastructure.
          </p>
          {social.length > 0 && (
            <ul className="mt-6 flex items-center gap-3" data-testid="footer-social">
              {social.map((s) => {
                const Icon = ICONS[s.key] || Globe;
                return (
                  <li key={s.key}>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      title={s.label}
                      className="inline-flex items-center justify-center w-9 h-9 border border-ec-line text-ec-ink hover:text-white hover:bg-ec-ink hover:border-ec-ink transition-colors rounded-sm"
                      data-testid={`footer-social-${s.key}`}
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="mt-6 eyebrow">© {new Date().getFullYear()} EthicCred · All rights reserved</p>
        </div>

        <FooterCol
          title="Platform"
          items={[
            ["For Students", "/for-students"],
            ["For Employers", "/employers"],
            ["For Institutions", "/institutions"],
            ["Get Recognized", "/get-recognized"],
          ]}
        />
        <FooterCol
          title="Resources"
          items={[
            ["How it works", "/#how"],
            ["Verify a passport", "/verify"],
            ["Pricing", "/pricing"],
            ["Trust & Safety", "/trust-safety"],
            ["Developers", "/developers"],
          ]}
        />
        <FooterCol
          title="Get started"
          items={[
            ["Register", "/register"],
            ["Sign in", "/login"],
            ["Request a Demo", "/demo"],
          ]}
        />
        <FooterCol
          title="Legal"
          testidPrefix="legal"
          items={[
            ...FOOTER_LEGAL_SLUGS.map((slug) => [LEGAL_DOCS[slug]?.title || slug, `/legal/${slug}`]),
            ["Sitemap", "/sitemap"],
          ]}
        />
      </div>
    </footer>
  );
}

function FooterCol({ title, items, testidPrefix }) {
  return (
    <div>
      <div className="eyebrow mb-4">{title}</div>
      <ul className="space-y-2">
        {items.map(([label, href]) => (
          <li key={label}>
            <Link
              to={href}
              className="text-sm text-ec-ink hover:text-ec-emerald transition-colors"
              data-testid={testidPrefix ? `${testidPrefix}-${href.split("/").pop()}` : undefined}
            >
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
